// @shared/stores/useRoomsStore.js
import { create } from "zustand";
import {
  createRoom,
  joinRoom,
  getLobbyRooms,
  subscribeToRoom,
} from "../utils/roomFunctions";
import { db } from "../firebase/config";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import AudioManager from "../utils/AudioManager";

export const useRoomsStore = create((set, get) => ({
  currentRoom: null,
  matchData: null,
  rooms: [],
  lastRoomDoc: null,
  roomUnsub: null,
  matchUnsub: null,
  chatMessages: [],
  chatUnsub: null,
  hasUnreadMessages: false,
  unreadCount: 0,
  hands: {}, // { [playerId]: cardArray }
  decks: {}, // { [playerId]: deckArray }
  chatDrawerOpen: false,

  setHand: (playerId, cards) =>
    set((state) => ({
      hands: { ...state.hands, [playerId]: cards },
    })),

  setDeck: (playerId, cards) =>
    set((state) => ({
      decks: { ...state.decks, [playerId]: cards },
    })),

  clearPlayerData: () =>
    set({
      hands: {},
      decks: {},
    }),

  setCurrentRoom: (room) => {
    localStorage.setItem("roomId", room.id);
    set({ currentRoom: room });
  },

  setMatchData: (data) => set({ matchData: data }),

  subscribeToMatch: (roomId, matchId = "match") => {
    const matchRef = doc(db, "rooms", roomId, "matchData", matchId);

    const unsub = onSnapshot(matchRef, (snapshot) => {
      if (snapshot.exists()) {
        set({ matchData: snapshot.data() });
      }
    });

    set({ matchUnsub: unsub });
  },

  subscribeToChatThread: (roomId, userId) => {
    const chatQuery = query(
      collection(db, "rooms", roomId, "chatThreadData"),
      orderBy("createdAt", "asc")
    );

    let prevMessageCount = 0; // keep track of previous message count

    const unsub = onSnapshot(chatQuery, (snapshot) => {
      const messages = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const nonSelfMessages = messages.filter((msg) => msg.senderId !== userId);
      const unreadCount = nonSelfMessages.length;

      // 🔔 Play SFX if there's a new non-self message
      const latest = messages[messages.length - 1];
      if (
        latest &&
        latest.senderId !== userId &&
        messages.length > prevMessageCount &&
        !useRoomsStore.getState().chatDrawerOpen // 👈 track this in state
      ) {
        // import("../utils/AudioManager").then((module) => {
        AudioManager.playSFX("liveChat");
        // });
      }

      prevMessageCount = messages.length;

      set({
        chatMessages: messages,
        hasUnreadMessages: unreadCount > 0,
        unreadCount,
      });
    });

    set({ chatUnsub: unsub });
  },

  clearUnreadMessages: () => set({ hasUnreadMessages: false, unreadCount: 0 }),

  setChatDrawerOpen: (isOpen) => set({ chatDrawerOpen: isOpen }),

  unsubscribeFromChatThread: () => {
    const { chatUnsub } = get();
    if (chatUnsub) {
      chatUnsub();
      set({ chatUnsub: null, chatMessages: [] });
    }
  },

  unsubscribeFromMatch: () => {
    const { matchUnsub } = get();
    if (matchUnsub) {
      matchUnsub();
      set({ matchUnsub: null, matchData: null });
    }
  },

  createAndSetRoom: async (userId, settings) => {
    const room = await createRoom(userId, settings);
    get().subscribeToRoom(room.id);
    get().subscribeToChatThread(room.id, userId);
    set({ currentRoom: room });
  },

  joinAndSetRoom: async (accessCode, userId) => {
    const room = await joinRoom(accessCode, userId);
    get().subscribeToRoom(room.id);
    get().subscribeToChatThread(room.id, userId);
    set({ currentRoom: room });
  },

  subscribeToRoom: (roomId) => {
    const unsub = subscribeToRoom(roomId, (roomData) => {
      const { currentRoom } = get();
      if (currentRoom && currentRoom.id === roomId) {
        set({ currentRoom: roomData });
      }
    });

    set({ roomUnsub: unsub });
  },

  unsubscribeFromRoom: () => {
    const { roomUnsub } = get();
    if (roomUnsub) {
      roomUnsub();
      set({ roomUnsub: null });
    }
  },

  clearRoom: () => {
    get().unsubscribeFromRoom();
    get().unsubscribeFromMatch();
    get().unsubscribeFromChatThread();
    localStorage.removeItem("roomId");
    set({ currentRoom: null });
  },

  fetchLobbyRooms: async () => {
    const { rooms, lastDoc } = await getLobbyRooms();
    set({ rooms, lastRoomDoc: lastDoc });
  },

  fetchMoreLobbyRooms: async () => {
    const { lastRoomDoc } = get();
    if (!lastRoomDoc) return;

    const { rooms: newRooms, lastDoc } = await getLobbyRooms(lastRoomDoc);
    set((state) => ({
      rooms: [...state.rooms, ...newRooms],
      lastRoomDoc: lastDoc,
    }));
  },

  clearMatch: () => set({ matchData: null }),
  clearRooms: () => set({ rooms: [], lastRoomDoc: null }),
}));
